import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Box,
} from '@mui/material';
import { usePendingQuery } from '../services/evidencePay';
import { styled } from '@mui/material/styles';
import checkAccessRights from '../utils/functions/accessRight';
import { ROLES } from '../utils/constants';
import { useSelector } from 'react-redux';
import Loading from './Loading';
import InputBase from '@mui/material/InputBase';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import { tableCellClasses } from '@mui/material/TableCell';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import addCommas from '../utils/functions/commas';
import { useCustomerManageQuery } from '../services/customerManage';
import SortableTableLabel from './Table/SortableTableLabel';
import axios from 'axios';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import TableDataContact from './Dialog/TableDataContact';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

function CustomerManage() {
  const { t } = useTranslation();
  const columnsContact = [
    { id: 'category', label: 'Contact Type', minWidth: 100, align: 'left' },
    {
      id: 'contact',
      label: 'Contact Name',
      minWidth: 100,
      align: 'center',
    },
    { id: 'tel', label: 'Telephone', minWidth: 100, align: 'center' },
    { id: 'email', label: 'Email', minWidth: 100, align: 'center' },
    { id: 'remark', label: 'Remark', minWidth: 100, align: 'center' },
  ];
  const columns = [
    { id: 'customer_no', label: t('navbar.custNo') },
    { id: 'customer_name', label: t('app.customerName') },
    { id: 'financial_name', label: t('app.financeOfficer') },
    { id: 'status', label: t('app.status') },
    { id: 'contact', label: t('app.contactInfo'), sortable: false },
    { id: 'online_payment', label: t('app.invoicePaymentType') },
    { id: 'billing_due', label: t('app.billingTerms') },
    { id: 'billing_type', label: t('app.billingType') },
    { id: 'billing_checking', label: t('app.billingWithReceipt') },
    { id: 'billing_document', label: t('app.billingAttachments') },
    { id: 'payment_type', label: t('app.paymentType') },
    { id: 'billing_contact', label: t('app.billingNotes') },
    { id: 'payment_contact', label: t('app.remark') },
  ];
  const [open, setOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState('customer_no');
  const [sortOrder, setSortOrder] = useState('asc');
  const [total, setTotal] = useState(10);
  const [contactData, setContactData] = useState(null);
  const [dataCust, setDataCust] = useState('');
  const [searchAll, setSearchAll] = useState('');
  const { value: merchant, allMerchant } = useSelector(
    (state) => state.merchant,
  );
  const isAdminRole = checkAccessRights(ROLES.ADMIN_ROLE);
  const sorgcode = isAdminRole ? merchant || '' : allMerchant || '';
  const shouldSkip = isAdminRole && !merchant;
  const [selectedOnlinePayment, setSelectedOnlinePayment] = useState({});
  const [billingDueType, setBillingDueType] = useState({});
  const [billDoc, setBillDoc] = useState({});
  const [receiptBill, setReceiptBill] = useState({});
  const [billContact, setBillContact] = useState({});
  const [paymentType, setPaymentType] = useState({});
  const [paymentContact, setPaymentContact] = useState({});
  const [howToBill, setHowToBill] = useState({});
  const [search, setSearch] = useState('');

  const customCellHeader = {
    whiteSpace: 'nowrap',
    backgroundColor: 'primary.main',
    color: 'white',
    textAlign: 'center', // จัดข้อความกึ่งกลางแนวนอน
    minWidth: '180px', // กำหนดความกว้างขั้นต่ำ
    width: '200px', // กำหนดความกว้างของเซลล์
    maxWidth: '300px', // กำหนดความกว้างสูงสุด (ไม่ให้เกินขนาดที่กำหนด)
  };

  const { data, isLoading, isFetching } = useCustomerManageQuery(
    {
      sorgcode,
      page: page + 1,
      size: rowsPerPage,
      keyword: search,
      sortBy,
      sortOrder,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: shouldSkip,
    },
  );

  useEffect(() => {
    if (data?.data?.customerManageList) {
      setDataCust(data?.data?.customerManageList);
    }
  }, [data]);

  useEffect(() => {
    if (data?.data?.total) {
      setTotal(data?.data?.total);
    }
  }, [data]);

  const handleSearchAll = (e) => {
    setSearch(e.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleRequestSort = (property) => {
    setSortOrder(
      sortBy === property && sortOrder === 'asc' ? 'desc' : 'asc',
    );
    setSortBy(property);
    setPage(0);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0); // เริ่มที่หน้า 0 เมื่อเปลี่ยนจำนวนแถวต่อหน้า
  };

  const handleClickOpen = (contactInfo) => {
    const formattedContacts = contactInfo?.map(
      (item) => Object.values(item)[0],
    );
    // ทำอย่างอื่นกับข้อมูล เช่น เก็บค่า contact ไว้ใน state แล้วเปิด dialog
    setSelectedContact(formattedContacts);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOnlinePaymentChange = (rowIndex, field, value) => {
    setSelectedOnlinePayment((prev) => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        [field]: value,
      },
    }));
  };

  const handleBillingDueTypeChange = (rowIndex, field, value) => {
    setBillingDueType((prev) => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        [field]: value,
      },
    }));
  };

  const handlePaymentContactChange = (rowIndex, field, value) => {
    setPaymentContact((prev) => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        [field]: value,
      },
    }));
  };

  const handlePaymentTypeChange = (rowIndex, field, value) => {
    setPaymentType((prev) => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        [field]: value,
      },
    }));
  };

  const handleReceiptBillChange = (rowIndex, field, value) => {
    setReceiptBill((prev) => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        [field]: value,
      },
    }));
  };
  const handleHowToBillChange = (rowIndex, field, value) => {
    setHowToBill((prev) => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        [field]: value,
      },
    }));
  };
  const handleBillDocChange = (rowIndex, field, value) => {
    setBillDoc((prev) => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        [field]: value,
      },
    }));
  };
  const handleBillContactChange = (rowIndex, field, value) => {
    setBillContact((prev) => ({
      ...prev,
      [rowIndex]: {
        ...prev[rowIndex],
        [field]: value,
      },
    }));
  };

  return (
    <div>
      <TableDataContact
      t={t}
        isFetching={isFetching}
        open={open}
        setOpen={setOpen}
        selectedContact={selectedContact}
      />
      <div className="flex-1">
        <div className="my-2 ml-2">
          <div className="text-lg font-light flex flex-wrap items-center justify-between">
            <div className="lg:w-3/4 w-1/2 font-bold">
              {t('app.customerManage')}
            </div>
            <div className="lg:w-1/4 w-1/2 ">
              <Paper
                component="form"
                sx={{
                  p: '2px 4px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <InputBase
                  className="app-search-input"
                  type="text"
                  sx={{ ml: 1, flex: 1 }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)} // <-- เพิ่ม onChange ตรงนี้
                  placeholder="ค้นหา"
                />

                <Divider sx={{ height: 28, m: 0.5 }} orientation="vertical" />

                <div className="p-2">
                  <SearchIcon sx={{ fontSize: '24px' }} color="primary" />
                </div>
              </Paper>
            </div>
          </div>
        </div>
        <div className="px-2 mt-6">
          <TableContainer
            component={Paper}
            sx={{ maxHeight: 650, overflow: 'auto' }}
          >
            <Table
              stickyHeader
              sx={{ minWidth: 1200 }}
              aria-label="simple table"
            >
             <TableHead>
  <TableRow>
    {columns.map((column) => (
      <TableCell key={column.id} sx={customCellHeader}>
        <SortableTableLabel
          column={column}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleRequestSort}
          color="white"
        />
      </TableCell>
    ))}
  </TableRow>
</TableHead>

              <TableBody>
                {isFetching ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : _.size(dataCust) ? (
                  dataCust.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        '&:last-child td, &:last-child th': { border: 0 },
                        height: 40,
                      }}
                    >
                      <TableCell>{row.customer_no}</TableCell>
                      <TableCell>{row.customer_name}</TableCell>
                      <TableCell>{row.financial_name}</TableCell>

                      <TableCell
                        sx={{
                          color: row.status === 'Active' ? 'green' : 'red',
                          fontWeight: 'bold',
                          textAlign: 'center',
                        }}
                      >
                        {row.status}
                      </TableCell>

                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleClickOpen(row.contact)} // ส่งค่า row.contact เข้าไป
                          sx={{ color: 'white' }}
                        >
                         {t('app.contactInfo')}
                        </Button>
                      </TableCell>

                      <TableCell>
                        <TextField
                          variant="outlined"
                          size="small"
                          value={
                            selectedOnlinePayment[index]?.online_payment ??
                            row.online_payment
                          }
                          onChange={(e) =>
                            handleOnlinePaymentChange(
                              index,
                              'online_payment',
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          variant="outlined"
                          size="small"
                          value={
                            billingDueType[index]?.billing_due ??
                            row.billing_due
                          }
                          onChange={(e) =>
                            handleBillingDueTypeChange(
                              index,
                              'billing_due',
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          variant="outlined"
                          size="small"
                          value={
                            howToBill[index]?.billing_type ?? row.billing_type
                          }
                          onChange={(e) =>
                            handleHowToBillChange(
                              index,
                              'billing_type',
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>

                      <TableCell>
                        {' '}
                        <TextField
                          variant="outlined"
                          size="small"
                          value={
                            receiptBill[index]?.billing_checking ??
                            row.billing_checking
                          }
                          onChange={(e) =>
                            handleReceiptBillChange(
                              index,
                              'billing_checking',
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>

                      <TableCell>
                        {' '}
                        <TextField
                          variant="outlined"
                          size="small"
                          value={
                            billDoc[index]?.billing_document ??
                            row.billing_document
                          }
                          onChange={(e) =>
                            handleBillDocChange(
                              index,
                              'billing_document',
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {' '}
                        <TextField
                          variant="outlined"
                          size="small"
                          value={
                            paymentType[index]?.payment_type ?? row.payment_type
                          }
                          onChange={(e) =>
                            handlePaymentTypeChange(
                              index,
                              'payment_type',
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>

                      <TableCell>
                        {' '}
                        <TextField
                          variant="outlined"
                          size="small"
                          value={
                            billContact[index]?.billing_contact ??
                            row.billing_contact
                          }
                          onChange={(e) =>
                            handleBillContactChange(
                              index,
                              'billing_contact',
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>

                      <TableCell>
                        <TextField
                          variant="outlined"
                          size="small"
                          value={
                            paymentContact[index]?.payment_contact ??
                            row.payment_contact
                          }
                          onChange={(e) =>
                            handlePaymentContactChange(
                              index,
                              'payment_contact',
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      <div>ไม่มีข้อมูล</div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} of ${addCommas(count, 0)}`
            }
          />
        </div>
      </div>
    </div>
  );
}

export default CustomerManage;
